// Knowledge-graph explorer. Pick a pipeline + KG version, see a force-
// directed visualization of the graph, run RAG queries against it, and
// inspect the docs/manifest/eval-quality side panels.
//
// Ported from the deleted apps/metaphysics frontend
// (mp-knowledge-graphs-view.ts) into idiomatic React + TanStack Router.

import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { useAuth } from '../auth/AuthProvider';
import { BackButton } from '../components/common/BackButton';
import { EmptyState } from '../components/common/EmptyState';
import { PageHeader } from '../components/common/PageHeader';
import { Spinner } from '../components/common/Spinner';
import '../components/common/markdown-prose.css';
import '../components/knowledge-graphs/knowledge-graphs.css';
import { ChunksModal } from '../components/knowledge-graphs/ChunksModal';
import { GraphCanvas } from '../components/knowledge-graphs/GraphCanvas';
import { KgIndexingProgress } from '../components/knowledge-graphs/KgIndexingProgress';
import { colorForEntityType } from '../components/knowledge-graphs/entity-color';
import type { IGraphEdge, IGraphNode } from '../components/knowledge-graphs/force-sim';
import { useDocumentSetKgVersions, useDocumentSets } from '../hooks/useDocumentSets';
import { routes } from '../lib/constants';
import { metaphysicsBaseUrl } from '../lib/metaphysics';
import type { IDocumentSet } from '../lib/metaphysics';
import {
  useMetaphysicsKg,
  type IDocInfo,
  type IEvalRunSummary,
  type IKgOwner,
  type IKgPipeline,
  type IKgSummary,
  type IKgVersion,
  type IKgWorkspaceProgress,
  type ILightragLlmRoleConfig,
} from '../lib/metaphysics-kg';

/**
 * Deep-link query params. `ownerKind=documentSet&ownerId=<uuid>&kgVersion=<n>`
 * is how the doc-set KG panel (`$setId.tsx`) navigates here. Pipeline mode is
 * the default — omit params and the page auto-selects the first pipeline.
 */
export interface IKgRouteSearch {
  ownerKind?: 'pipeline' | 'documentSet';
  ownerId?: string;
  kgVersion?: number;
}

export const Route = createFileRoute('/knowledge-graphs')({
  component: KnowledgeGraphsPage,
  validateSearch: (raw: Record<string, unknown>): IKgRouteSearch => {
    const out: IKgRouteSearch = {};
    if (raw.ownerKind === 'pipeline' || raw.ownerKind === 'documentSet') {
      out.ownerKind = raw.ownerKind;
    }
    if (typeof raw.ownerId === 'string') out.ownerId = raw.ownerId;
    const v = typeof raw.kgVersion === 'string' ? Number(raw.kgVersion) : raw.kgVersion;
    if (typeof v === 'number' && Number.isFinite(v)) out.kgVersion = v;
    return out;
  },
});

type IKgOwnerKind = 'pipeline' | 'documentSet';

/**
 * localStorage key for the last-selected KG on this page. Deep-link params
 * always win over the persisted value; persistence only kicks in when the URL
 * has no `ownerId`. Stored shape: `{"kind":"pipeline"|"documentSet","id":"…"}`.
 */
const kgLastSelectionLsKey = 'cortex:kg-page:last-selection';

interface IKgSelection {
  kind: IKgOwnerKind;
  id: string;
}

function readLastSelection(): IKgSelection | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(kgLastSelectionLsKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed !== null &&
      typeof parsed === 'object' &&
      'kind' in parsed &&
      'id' in parsed &&
      typeof (parsed as { id: unknown }).id === 'string' &&
      ((parsed as { kind: unknown }).kind === 'pipeline' ||
        (parsed as { kind: unknown }).kind === 'documentSet')
    ) {
      return parsed as IKgSelection;
    }
  } catch {
    /* corrupt entry — ignore */
  }
  return null;
}

function writeLastSelection(sel: IKgSelection | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (sel === null) {
      window.localStorage.removeItem(kgLastSelectionLsKey);
    } else {
      window.localStorage.setItem(kgLastSelectionLsKey, JSON.stringify(sel));
    }
  } catch {
    /* quota / disabled storage — silently drop */
  }
}

// ── Local types ───────────────────────────────────────────────────────────

interface IQueryResult {
  text: string;
  answer: string;
  entities: string[];
  mode: string;
  ts: string;
}

// Order matters: leftmost is the default and the first option in the dropdown.
// "mix" leads because it combines naive chunk search + hybrid graph traversal —
// for procedural questions (alarm response, sequence steps) the narrative lives
// in chunks while the structural context lives in the graph; mix is the only
// mode that retrieves both. The other modes are kept for explicit selection.
const queryModes = ['mix', 'hybrid', 'local', 'global', 'naive'] as const;
type IQueryMode = (typeof queryModes)[number];

const heatDecay = 0.92;

function nowTs(): string {
  return `${new Date().toISOString().substring(11, 19)} UTC`;
}

// ── Component ─────────────────────────────────────────────────────────────

/**
 * Primary KG explorer — workspace graphs only.
 *
 * Pipeline-owned graphs live at `/internal/knowledge-graphs` (Legacy in the
 * nav). See `ownerKinds` on IKnowledgeGraphsViewProps for why this filter is
 * owner-kind rather than a true workspace flag.
 */
function KnowledgeGraphsPage() {
  const search = Route.useSearch();
  return <KnowledgeGraphsView search={search} ownerKinds={['documentSet']} />;
}

export interface IKnowledgeGraphsViewProps {
  search: IKgRouteSearch;
  /**
   * Pin the view to one KG owner. The owner picker is hidden, the auto-select
   * and localStorage-persistence effects are skipped, and the version selector
   * stays. Used by the workspace Query tab, which only ever shows its own KG.
   */
  lockedOwner?: IKgSelection;
  /** Drop the page header + back button when rendered inside another page. */
  embedded?: boolean;
  /**
   * Which owner kinds the picker offers. Defaults to both.
   *
   * NOTE: a workspace IS a DocumentSet at the DB layer — there is no column
   * distinguishing one created via /workspaces from one created via the legacy
   * /document-sets UI. So `['documentSet']` is as close to "workspaces only" as
   * the schema allows: it excludes pipeline-owned KGs, nothing more.
   */
  ownerKinds?: IKgOwnerKind[];
}

/**
 * Grows an element to fill down to the bottom of its scroll container instead
 * of guessing the chrome height with a magic `calc(100vh - Npx)`. That guess
 * can't know the real height above the card, which differs between the
 * standalone `/knowledge-graphs` page (page header) and the embedded workspace
 * Query tab (tab strip) — leaving a dead band at the bottom in one or both.
 *
 * Instead we measure: find the nearest scrollable ancestor, sum the
 * padding-bottom of everything between us and it (the gutter that must stay
 * below the card), and size so the card's bottom lands exactly there. Runs in
 * a layout effect and re-runs on resize and any reflow above us, so it stays
 * correct without per-context magic numbers. Returns `null` until first
 * measurement, so callers keep a sensible pre-paint fallback.
 */
function useFillToScrollBottom(minPx: number) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number | null>(null);

  const recompute = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // Nearest scrollable ancestor (the shell's <main> in constrained mode).
    let scroller: HTMLElement | null = el.parentElement;
    while (scroller) {
      const oy = getComputedStyle(scroller).overflowY;
      if (oy === 'auto' || oy === 'scroll') break;
      scroller = scroller.parentElement;
    }
    const container = scroller ?? (document.scrollingElement as HTMLElement | null);
    if (!container) return;
    // Bottom of the scroller's content/padding box in viewport coords.
    const scrollerBottom = container.getBoundingClientRect().top + container.clientHeight;
    // Sum padding-bottom of every ancestor up to the scroller — that space has
    // to remain below the card, otherwise growing into it forces a scrollbar.
    let inset = 0;
    let node: HTMLElement | null = el;
    while (node && node !== container && node.parentElement) {
      inset += Number.parseFloat(getComputedStyle(node.parentElement).paddingBottom) || 0;
      node = node.parentElement;
    }
    const rect = el.getBoundingClientRect();
    const delta = scrollerBottom - inset - rect.bottom;
    // `- 1` absorbs sub-pixel rounding so we never tip into a 1px scrollbar.
    const next = Math.max(minPx, Math.floor(rect.height + delta) - 1);
    setHeight((prev) => (prev === next ? prev : next));
  }, [minPx]);

  useLayoutEffect(() => {
    recompute();
    // A second pass after first paint catches late-mounting chrome above us
    // (the portaled page header, async banners) that shifts our top offset.
    const raf = requestAnimationFrame(recompute);
    window.addEventListener('resize', recompute);
    // Reflow anywhere in the document (tab switches, banners) can move us.
    const ro = new ResizeObserver(recompute);
    ro.observe(document.body);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', recompute);
      ro.disconnect();
    };
  }, [recompute]);

  return { ref, height };
}

export function KnowledgeGraphsView({
  search,
  lockedOwner,
  embedded = false,
  ownerKinds = ['pipeline', 'documentSet'],
}: IKnowledgeGraphsViewProps) {
  const kg = useMetaphysicsKg();
  const locked = lockedOwner ?? null;
  const allowsPipelines = ownerKinds.includes('pipeline');
  const allowsDocumentSets = ownerKinds.includes('documentSet');
  // Fill the graph card down to the viewport bottom instead of a fixed calc().
  const { ref: kgRootRef, height: kgRootHeight } = useFillToScrollBottom(540);

  // ── Selection state ────────────────────────────────────────────────────
  //
  // The page can render KGs from either a Pipeline (existing) or a
  // DocumentSet (new direct path). The two share the force-directed canvas,
  // doc list, and entity-type histogram. The RAG query box and eval panel
  // are pipeline-only for v1 (cortex eval-harness is keyed on pipelineId).
  //
  // Owner is selected via a single grouped dropdown; the sub-tab toggle
  // was retired for consistency with the `rag-retrieve` node picker.
  //
  // Initial-state precedence:
  //   1. Deep-link (?ownerKind=…&ownerId=…&kgVersion=…) — always wins.
  //   2. localStorage `cortex:kg-page:last-selection` — page-scoped memory.
  //   3. Auto-select the first pipeline once the list arrives.
  const persistedSelection = useMemo(() => (locked ? null : readLastSelection()), [locked]);
  const initialSelection: IKgSelection | null =
    locked ??
    (search.ownerKind && search.ownerId
      ? { kind: search.ownerKind, id: search.ownerId }
      : persistedSelection);

  const [ownerKind, setOwnerKind] = useState<IKgOwnerKind>(
    initialSelection?.kind ?? (allowsPipelines ? 'pipeline' : 'documentSet'),
  );

  // ── Owner picker data ──────────────────────────────────────────────────
  // Both lists are always needed by the unified dropdown, so no `enabled`
  // guard on pipelines.
  const pipelines = useQuery({
    queryKey: ['mp', 'kg', 'pipelines'],
    queryFn: () => kg.listPipelines(),
  });
  const documentSets = useDocumentSets();

  // Slim "which owners actually have a KG?" summary — drives the dropdown
  // filter so we don't show pipelines/docsets that were never synced.
  // Single round-trip; refreshed on the same cadence as its consumers.
  const kgOwners = useQuery({
    queryKey: ['mp', 'kg', 'owners'],
    queryFn: () => kg.listOwners(),
  });
  const hasKgByOwner = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const p of kgOwners.data?.pipelines ?? []) {
      m.set(`pipeline:${p.id}`, p.latestKgVersion !== null);
    }
    for (const d of kgOwners.data?.documentSets ?? []) {
      m.set(`documentSet:${d.id}`, d.latestKgVersion !== null);
    }
    return m;
  }, [kgOwners.data]);
  // Filtered projections passed to the dropdown. Owners without a KG are
  // hidden entirely (per user request — the alternative "grey out" would
  // still let a click land on an empty explorer).
  const pipelinesWithKg = useMemo(() => {
    if (!allowsPipelines) return [];
    if (!pipelines.data) return undefined;
    // Until the owners summary lands, don't filter — otherwise the UI
    // flickers "empty" for the first paint.
    if (!kgOwners.data) return pipelines.data;
    return pipelines.data.filter((p) => hasKgByOwner.get(`pipeline:${p.id}`) === true);
  }, [allowsPipelines, pipelines.data, kgOwners.data, hasKgByOwner]);
  const documentSetsWithKg = useMemo(() => {
    if (!allowsDocumentSets) return [];
    if (!documentSets.data) return undefined;
    if (!kgOwners.data) return documentSets.data;
    return documentSets.data.filter((d) => hasKgByOwner.get(`documentSet:${d.id}`) === true);
  }, [allowsDocumentSets, documentSets.data, kgOwners.data, hasKgByOwner]);

  const [pipelineId, setPipelineId] = useState<string | null>(
    initialSelection?.kind === 'pipeline' ? initialSelection.id : null,
  );
  const [documentSetId, setDocumentSetId] = useState<string | null>(
    initialSelection?.kind === 'documentSet' ? initialSelection.id : null,
  );
  /** null = latest. */
  const [kgVersion, setKgVersion] = useState<number | null>(search.kgVersion ?? null);

  // Auto-select the first item in the active list once it arrives. Prefer
  // the KG-filtered projection so a fresh dev environment doesn't land on
  // an owner with no KG (which would show an empty explorer + a stale
  // "Info · 0 docs" chip).
  useEffect(() => {
    if (locked) return;
    if (ownerKind === 'pipeline' && pipelineId === null && pipelinesWithKg?.length) {
      const first = pipelinesWithKg[0];
      if (first) setPipelineId(first.id);
    }
  }, [locked, ownerKind, pipelineId, pipelinesWithKg]);
  useEffect(() => {
    if (locked) return;
    if (ownerKind === 'documentSet' && documentSetId === null && documentSetsWithKg?.length) {
      const first = documentSetsWithKg[0];
      if (first) setDocumentSetId(first.id);
    }
  }, [locked, ownerKind, documentSetId, documentSetsWithKg]);

  // Persist the current selection so a reload restores it. Skipped when the
  // active id is null (initial render / auto-select in flight) so a stale
  // pointer never gets wiped by a transient null, and when the view is locked
  // to an owner — an embedded workspace tab must not hijack the page's memory.
  useEffect(() => {
    if (locked) return;
    const activeId = ownerKind === 'pipeline' ? pipelineId : documentSetId;
    if (!activeId) return;
    writeLastSelection({ kind: ownerKind, id: activeId });
  }, [locked, ownerKind, pipelineId, documentSetId]);

  // ── Derived: the active owner (or null while loading the picker) ────────
  // ── LightRAG version ────────────────────────────────────────────────────
  // Proxied via metaphysics (which talks to LightRAG directly) so the console
  // doesn't need a hardcoded LightRAG URL or CORS exception. Public endpoint,
  // no auth needed — version info is non-sensitive.
  const lightragVersion = useQuery({
    queryKey: ['lightrag', 'version'],
    queryFn: async () => {
      try {
        const res = await fetch(`${metaphysicsBaseUrl}/lightrag/version`, {
          signal: AbortSignal.timeout(3000),
        });
        if (!res.ok) return null;
        const data = (await res.json()) as { lightrag_hku_version?: string };
        return data.lightrag_hku_version || null;
      } catch {
        return null;
      }
    },
    staleTime: 1000 * 60 * 5,
  });

  // ── LightRAG per-role default models ────────────────────────────────────
  // Fetched once and cached — paired with each pipeline's lightragLlmRoleConfig
  // override to render the effective per-role model in the info sidebar.
  const lightragRoleDefaults = useQuery({
    queryKey: ['lightrag', 'role-defaults'],
    queryFn: async () => {
      try {
        const res = await fetch(`${metaphysicsBaseUrl}/lightrag/role-config-defaults`, {
          signal: AbortSignal.timeout(3000),
        });
        if (!res.ok) return null;
        return (await res.json()) as { defaults: ILightragLlmRoleConfig; fallback: string };
      } catch {
        return null;
      }
    },
    staleTime: 1000 * 60 * 5,
  });

  const owner: IKgOwner | null = useMemo(() => {
    if (ownerKind === 'pipeline') {
      return pipelineId ? { kind: 'pipeline', pipelineId } : null;
    }
    return documentSetId ? { kind: 'documentSet', documentSetId } : null;
  }, [ownerKind, pipelineId, documentSetId]);

  // ── Versions + graph (owner-discriminated) ──────────────────────────────
  // `enabled: !!owner` already guards the queryFn from running when owner
  // is null, but TS doesn't narrow into the queryFn closure. Use an
  // explicit throw so the narrowing is local + lint-clean (no
  // non-null-assertion needed).
  const versions = useQuery({
    queryKey: ['mp', 'kg', 'versions', ownerKind, pipelineId, documentSetId],
    queryFn: () => {
      if (!owner) throw new Error('unreachable: versions queryFn ran with null owner');
      return kg.listVersions(owner);
    },
    enabled: !!owner,
  });

  const graph = useQuery({
    queryKey: ['mp', 'kg', 'graph', ownerKind, pipelineId, documentSetId, kgVersion],
    queryFn: () => {
      if (!owner) throw new Error('unreachable: graph queryFn ran with null owner');
      return kg.getGraph(owner, kgVersion);
    },
    enabled: !!owner,
  });

  const { nodes, edges, nodeIndex } = useMemo(() => {
    if (!graph.data) {
      return {
        nodes: [] as IGraphNode[],
        edges: [] as IGraphEdge[],
        nodeIndex: new Map<string, IGraphNode>(),
      };
    }
    const ids = new Set(graph.data.nodes.map((n) => n.id));
    const ns: IGraphNode[] = graph.data.nodes.map((n) => {
      const labelArr = Array.isArray(n.labels) ? n.labels : [];
      const label = (labelArr[0] ?? n.label ?? n.id) as string;
      const entityType = (n.properties?.['entity_type'] as string) ?? '';
      const color = colorForEntityType(entityType);
      return {
        id: n.id,
        label,
        entityType,
        description: String(n.properties?.['description'] ?? '').replace(/<SEP>/g, '\n'),
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        radius: 7,
        color,
        glowColor: color,
        pinned: false,
      };
    });
    const es: IGraphEdge[] = graph.data.edges
      .filter((e) => ids.has(e.source) && ids.has(e.target) && e.source !== e.target)
      .map((e) => ({
        source: e.source,
        target: e.target,
        description: String(e.properties?.['description'] ?? ''),
      }));
    return { nodes: ns, edges: es, nodeIndex: new Map(ns.map((n) => [n.id, n])) };
  }, [graph.data]);

  // ── Centrality (degree rank) — computed once per graph load ────────────
  const centralityRank = useMemo(() => {
    const degree = new Map<string, number>();
    for (const n of nodes) degree.set(n.id, 0);
    for (const e of edges) {
      degree.set(e.source, (degree.get(e.source) ?? 0) + 1);
      degree.set(e.target, (degree.get(e.target) ?? 0) + 1);
    }
    const sorted = [...degree.entries()].sort((a, b) => b[1] - a[1]);
    const rank = new Map<string, number>();
    let lastDeg = -1;
    let lastRank = 0;
    sorted.forEach(([id, deg], idx) => {
      if (deg !== lastDeg) {
        lastRank = idx + 1;
        lastDeg = deg;
      }
      rank.set(id, lastRank);
    });
    return rank;
  }, [nodes, edges]);

  // ── Selection / hover ──────────────────────────────────────────────────
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // ── Search (find node by label) ────────────────────────────────────────
  const [searchText, setSearchText] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchResults = useMemo(() => {
    if (!searchText.trim()) return [] as IGraphNode[];
    const q = searchText.toLowerCase();
    return nodes.filter((n) => n.label.toLowerCase().includes(q)).slice(0, 20);
  }, [searchText, nodes]);

  // ── RAG query state ────────────────────────────────────────────────────
  const [queryText, setQueryText] = useState('');
  const [queryMode, setQueryMode] = useState<IQueryMode>('mix');
  const [queryModeOpen, setQueryModeOpen] = useState(false);
  const [queryResult, setQueryResult] = useState<IQueryResult | null>(null);
  const [queryPanelOpen, setQueryPanelOpen] = useState(false);
  const [recentQueries, setRecentQueries] = useState<IQueryResult[]>([]);
  const [showRecentMenu, setShowRecentMenu] = useState(false);
  const [participatingIds, setParticipatingIds] = useState<Set<string>>(new Set());
  /** Currently-highlighted entity-type (lower-case). Distinct from
   *  query-result-driven `participatingIds`: when set, the canvas
   *  highlights every node of this type. Toggled by clicking type
   *  badges in the Info-sidebar's Entity types section. */
  const [selectedType, setSelectedType] = useState<string | null>(null);

  // ── Heat-map state ─────────────────────────────────────────────────────
  const [heatEnabled, setHeatEnabled] = useState(false);
  const [heatScores, setHeatScores] = useState<Map<string, number>>(new Map());
  const [edgeHeatScores, setEdgeHeatScores] = useState<Map<string, number>>(new Map());
  const [heatHits, setHeatHits] = useState<Map<string, number>>(new Map());
  const [heatQueries, setHeatQueries] = useState<Map<string, IQueryResult[]>>(new Map());
  const [heatGamma, setHeatGamma] = useState(0.5);

  // Reset graph-coupled state whenever the owner / version changes.
  useEffect(() => {
    setSelectedId(null);
    setHoveredId(null);
    setQueryResult(null);
    setQueryPanelOpen(false);
    setParticipatingIds(new Set());
    setSelectedType(null);
  }, [ownerKind, pipelineId, documentSetId, kgVersion]);

  // When the user toggles a type, push the affected node IDs into
  // `participatingIds` so the existing canvas highlight path picks
  // them up. Toggling off (null) clears both.
  const onSelectType = (type: string | null): void => {
    setSelectedType(type);
    if (type === null) {
      setParticipatingIds(new Set());
      return;
    }
    const ids = nodes.filter((n) => (n.entityType || '').toLowerCase() === type).map((n) => n.id);
    setParticipatingIds(new Set(ids));
    setQueryPanelOpen(false);
    setQueryResult(null);
  };

  // ── Documents (with polling while indexing) ─────────────────────────────
  const docsQuery = useQuery({
    queryKey: ['mp', 'kg', 'docs', ownerKind, pipelineId, documentSetId, kgVersion],
    queryFn: () => {
      if (!owner) throw new Error('unreachable: docs queryFn ran with null owner');
      return kg.listDocs(owner, kgVersion);
    },
    enabled: !!owner,
    refetchInterval: (q) => {
      const data = q.state.data?.documents;
      if (!data) return false;
      // Poll fast while ANY doc is still in-flight — includes `analyzing`,
      // not just pending/processing. Otherwise we'd back off to 30s as soon
      // as the first batch moves past `processing` even though `analyzing`
      // docs are still working.
      const active = data.some(
        (d) => d.status === 'pending' || d.status === 'analyzing' || d.status === 'processing',
      );
      return active ? 5_000 : 30_000;
    },
  });
  const docs = docsQuery.data?.documents ?? [];
  const workspaceProgress = docsQuery.data?.workspaceProgress ?? null;
  // In-flight = anything not in a terminal state. LightRAG cycles docs
  // through pending → analyzing → processing → processed; the first three
  // are all "still working", only `processed` (and `failed`) are terminal.
  const indexingCount = docs.filter(
    (d) => d.status === 'pending' || d.status === 'analyzing' || d.status === 'processing',
  ).length;
  const failedCount = docs.filter((d) => d.status === 'failed').length;

  // ── Document-set sync-job state (for indexing indicator) ───────────────
  //
  // `indexingCount` only reflects docs already pushed into LightRAG — docs
  // still awaiting Convert / Push don't show up here. For documentSet-owned
  // KGs we also consult the sync-job's coarse state (pending/converting/
  // pushing/indexing) so the "indexing" pill fires while the sync is
  // running but per-doc rows haven't landed in LightRAG yet. The hook
  // polls on its own while the latest row is in-flight.
  const docsetVersions = useDocumentSetKgVersions(
    ownerKind === 'documentSet' ? (documentSetId ?? undefined) : undefined,
  );
  const docsetSyncRow = useMemo(() => {
    if (ownerKind !== 'documentSet' || !docsetVersions.data) return null;
    if (kgVersion === null) return docsetVersions.data[0] ?? null;
    return docsetVersions.data.find((v) => v.version === kgVersion) ?? null;
  }, [ownerKind, docsetVersions.data, kgVersion]);
  const docsetSyncInFlight = (() => {
    const s = docsetSyncRow?.syncJobState;
    return s === 'pending' || s === 'converting' || s === 'pushing' || s === 'indexing';
  })();
  const isIndexing = indexingCount > 0 || docsetSyncInFlight;

  // Filter the version-picker to hide KG versions whose sync landed in a
  // terminal-bad state — their LightRAG workspace is missing or partial,
  // so selecting them would show empty/broken graphs. Only applies to
  // documentSet owners; pipeline manifests are only written on successful
  // sync so the pipeline list is already clean.
  const visibleVersions = useMemo(() => {
    if (!versions.data) return [] as IKgVersion[];
    if (ownerKind !== 'documentSet' || !docsetVersions.data) return versions.data;
    const bad = new Set<number>();
    for (const r of docsetVersions.data) {
      if (
        r.syncJobState === 'failed' ||
        r.syncJobState === 'cancelled' ||
        r.syncJobState === 'stale'
      ) {
        bad.add(r.version);
      }
    }
    return versions.data.filter((v) => !bad.has(v.version));
  }, [versions.data, ownerKind, docsetVersions.data]);

  // ── KG quality (recent eval runs — pipeline-only) ──────────────────────
  //
  // The cortex eval-harness model is keyed on pipelineId; there's no
  // documentSet equivalent yet. Skip the query when ownerKind=documentSet
  // so the page doesn't fan out useless fetches.
  const evalRuns = useQuery({
    queryKey: ['mp', 'kg', 'eval-runs', pipelineId],
    queryFn: async () => {
      const all = await kg.listEvalRuns(50);
      const filtered = all.filter(
        (r) => r.config?.['pipelineId'] === pipelineId && r.config?.['evalFramework'] === 'ragas',
      );
      const summaries = await Promise.all(
        filtered.slice(0, 10).map(async (r) => {
          try {
            return await kg.getEvalRun(r.id);
          } catch (err) {
            console.warn('knowledge-graphs:getEvalRun', err);
            return null;
          }
        }),
      );
      return summaries.filter((r): r is IEvalRunSummary => r !== null);
    },
    enabled: ownerKind === 'pipeline' && !!pipelineId,
  });

  // ── RAG query mutation ─────────────────────────────────────────────────
  //
  // Routes against the active owner (pipeline or documentSet). Critical:
  // earlier this used the bare `pipelineId` state regardless of ownerKind,
  // which silently misrouted documentSet queries to whatever pipeline
  // happened to be auto-selected at page load. Now derived from `owner`
  // so the workspace is always the one the user picked.
  const runQuery = useMutation({
    mutationFn: async (text: string) => {
      if (!owner) {
        throw new Error(
          ownerKind === 'pipeline'
            ? 'Select a pipeline before running a query.'
            : 'Select a document set before running a query.',
        );
      }
      const data = await kg.query({
        query: text,
        mode: queryMode,
        ...(owner.kind === 'pipeline'
          ? { pipelineId: owner.pipelineId }
          : { documentSetId: owner.documentSetId }),
        ...(kgVersion !== null ? { kgVersion } : {}),
      });
      return data;
    },
    onSuccess: (data, text) => {
      const result: IQueryResult = {
        text,
        answer: data.answer ?? '',
        entities: data.entities ?? [],
        mode: queryMode,
        ts: nowTs(),
      };
      applyQueryResult(result);
      setQueryText('');
    },
    onError: (err: unknown, text) => {
      setQueryResult({
        text,
        answer: `Error: ${err instanceof Error ? err.message : String(err)}`,
        entities: [],
        mode: queryMode,
        ts: nowTs(),
      });
      setQueryPanelOpen(true);
    },
  });

  function applyQueryResult(result: IQueryResult) {
    setQueryResult(result);
    setRecentQueries((prev) => [result, ...prev.slice(0, 7)]);
    setQueryPanelOpen(true);
    setShowRecentMenu(false);

    const ids = new Set(nodes.filter((n) => result.entities.includes(n.label)).map((n) => n.id));
    setParticipatingIds(ids);
    setSelectedId(null);

    // Decay + bump per-node and per-edge heat.
    setHeatScores((prev) => {
      const next = new Map<string, number>();
      for (const [id, v] of prev) next.set(id, v * heatDecay);
      for (const id of ids) next.set(id, (next.get(id) ?? 0) + 1.0);
      return next;
    });
    setEdgeHeatScores((prev) => {
      const next = new Map<string, number>();
      for (const [k, v] of prev) next.set(k, v * heatDecay);
      for (const e of edges) {
        if (ids.has(e.source) && ids.has(e.target)) {
          const k = `${e.source}|${e.target}`;
          next.set(k, (next.get(k) ?? 0) + 1.0);
        }
      }
      return next;
    });
    setHeatHits((prev) => {
      const next = new Map(prev);
      for (const id of ids) next.set(id, (next.get(id) ?? 0) + 1);
      return next;
    });
    setHeatQueries((prev) => {
      const next = new Map(prev);
      for (const id of ids) {
        const log = next.get(id) ?? [];
        next.set(id, [result, ...log].slice(0, 5));
      }
      return next;
    });
  }

  function clearHighlight() {
    setParticipatingIds(new Set());
  }

  function clearHeatmap() {
    setHeatScores(new Map());
    setEdgeHeatScores(new Map());
    setHeatHits(new Map());
    setHeatQueries(new Map());
  }

  function focusNode(node: IGraphNode) {
    setSelectedId(node.id);
    setSearchText('');
    setSearchOpen(false);
  }

  // ── Manifest / eval panel toggles ──────────────────────────────────────
  const [manifestPanelOpen, setManifestPanelOpen] = useState(false);

  // KG summary (type histogram + per-type examples). Derived in-page
  // from the already-loaded `nodes`/`edges` arrays (the same bounded
  // BFS slice the canvas renders) so the sidebar counts ALWAYS match
  // the canvas overlay — no second API call, no two-different-caps
  // confusion.
  const kgSummary = useMemo<IKgSummary | null>(() => {
    if (!graph.data) return null;
    type Bucket = { type: string; count: number; examples: string[] };
    const buckets = new Map<string, Bucket>();
    for (const n of nodes) {
      const type = (n.entityType || 'unknown').toString();
      let b = buckets.get(type);
      if (!b) {
        b = { type, count: 0, examples: [] };
        buckets.set(type, b);
      }
      b.count += 1;
      if (b.examples.length < 3 && n.label) b.examples.push(n.label);
    }
    return {
      workspace: '',
      version: kgVersion,
      totalNodes: nodes.length,
      totalEdges: edges.length,
      truncated: false,
      entityTypes: [...buckets.values()].sort((a, b) => b.count - a.count),
    };
  }, [graph.data, nodes, edges, kgVersion]);

  const activeManifest: IKgVersion | null = useMemo(() => {
    const list = versions.data ?? [];
    if (kgVersion !== null) return list.find((v) => v.version === kgVersion) ?? null;
    return list[0] ?? null;
  }, [versions.data, kgVersion]);
  // Latest = latest *successfully-synced* version, so the "Latest · vN"
  // label in the dropdown doesn't point at a hidden failed/cancelled row.
  const latestVersion = visibleVersions[0]?.version ?? null;

  const selectedNode = selectedId ? (nodeIndex.get(selectedId) ?? null) : null;
  const hoveredNode = hoveredId ? (nodeIndex.get(hoveredId) ?? null) : null;

  const connectedNodes = useMemo(() => {
    if (!selectedNode) return [] as { peer: IGraphNode; dir: '→' | '←'; desc: string }[];
    return edges
      .filter((e) => e.source === selectedNode.id || e.target === selectedNode.id)
      .map((e) => {
        const peerId = e.source === selectedNode.id ? e.target : e.source;
        const peer = nodeIndex.get(peerId);
        const dir: '→' | '←' = e.source === selectedNode.id ? '→' : '←';
        return peer ? { peer, dir, desc: e.description } : null;
      })
      .filter((r): r is { peer: IGraphNode; dir: '→' | '←'; desc: string } => r !== null);
  }, [selectedNode, edges, nodeIndex]);

  // ── Render ─────────────────────────────────────────────────────────────
  // The pipeline list only feeds the owner picker, so a view that is locked to
  // an owner, or that excludes pipelines entirely, neither waits on it nor
  // fails with it.
  const pipelinesAreRelevant = !locked && allowsPipelines;
  if (pipelinesAreRelevant && pipelines.isLoading) return <Spinner />;
  if (pipelinesAreRelevant && pipelines.error) {
    return <div className="alert alert--danger">{(pipelines.error as Error).message}</div>;
  }
  if (pipelinesAreRelevant && !pipelines.data?.length) {
    return (
      <>
        <PageHeader
          title="Knowledge graphs"
          actions={<BackButton to={routes.INTERNAL} label="Internal" />}
        />
        <EmptyState
          title="No pipelines yet"
          description="Create a pipeline first — knowledge graphs are built from a pipeline's knowledge sources."
        />
      </>
    );
  }

  const pipeline = pipelines.data?.find((p) => p.id === pipelineId);
  const documentSet = documentSets.data?.find((d) => d.id === documentSetId);
  // For the LLM Roles editor, surface whichever owner is active (pipeline or
  // docset). Both shapes expose `lightragLlmRoleConfig` in the same form.
  const activeRoleOverride =
    ownerKind === 'pipeline' ? pipeline?.lightragLlmRoleConfig : documentSet?.lightragLlmRoleConfig;

  return (
    <>
      {embedded ? null : (
        <PageHeader
          title="Knowledge graphs"
          actions={<BackButton to={routes.INTERNAL} label="Internal" />}
        />
      )}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div
          ref={kgRootRef}
          className="kg-root"
          style={{
            // Measured fill (see useFillToScrollBottom) grows the card down to
            // the scroll container's bottom, so the graph fills the page in both
            // the standalone view and the embedded workspace Query tab without a
            // magic viewport-height offset. The calc() is only the pre-paint
            // fallback before the first measurement lands.
            height: kgRootHeight ?? (embedded ? 'calc(100vh - 250px)' : 'calc(100vh - 240px)'),
            minHeight: 540,
            display: 'flex',
          }}
        >
          {/* Top bar: unified KG-source picker + version + heat + info */}
          <PipelineBar
            hideOwnerPicker={Boolean(locked)}
            pipelines={pipelinesWithKg}
            documentSets={documentSetsWithKg}
            selection={
              ownerKind === 'pipeline'
                ? pipelineId
                  ? { kind: 'pipeline', id: pipelineId }
                  : null
                : documentSetId
                  ? { kind: 'documentSet', id: documentSetId }
                  : null
            }
            onSelect={(sel) => {
              // Switch owner-kind and set the corresponding id in one commit.
              // Reset dependent state (version, manifest panel, highlight) so
              // the graph re-fetches cleanly for the new owner.
              setOwnerKind(sel.kind);
              if (sel.kind === 'pipeline') {
                setPipelineId(sel.id);
              } else {
                setDocumentSetId(sel.id);
              }
              setKgVersion(null);
              setManifestPanelOpen(false);
              clearHighlight();
            }}
            versions={visibleVersions}
            kgVersion={kgVersion}
            latestVersion={latestVersion}
            onSelectVersion={setKgVersion}
            heatEnabled={heatEnabled}
            heatScoreCount={heatScores.size}
            heatGamma={heatGamma}
            onToggleHeat={() => setHeatEnabled((v) => !v)}
            onChangeGamma={setHeatGamma}
            onClearHeat={clearHeatmap}
            manifestPanelOpen={manifestPanelOpen}
            onToggleManifest={() => setManifestPanelOpen((v) => !v)}
            indexingCount={indexingCount}
            syncInFlight={docsetSyncInFlight}
            docCount={docs.length}
            onReload={() => {
              graph.refetch().catch(() => undefined);
              versions.refetch().catch(() => undefined);
              docsQuery.refetch().catch(() => undefined);
            }}
            loading={graph.isFetching}
          />

          {/* Canvas + side panels */}
          <div className="kg-canvas-area">
            <div className="kg-stage">
              {graph.isLoading ? (
                <div className="kg-overlay">
                  <div className="kg-spinner" />
                  <span>Loading graph…</span>
                </div>
              ) : graph.error ? (
                <div className="kg-overlay">
                  <span className="kg-error">⚠ {(graph.error as Error).message}</span>
                  <button
                    className="kg-btn"
                    onClick={() => {
                      graph.refetch().catch(() => undefined);
                    }}
                  >
                    Retry
                  </button>
                </div>
              ) : null}

              {!graph.isLoading && !graph.error && nodes.length > 0 ? (
                <GraphCanvas
                  nodes={nodes}
                  edges={edges}
                  participatingIds={participatingIds}
                  heatEnabled={heatEnabled}
                  heatScores={heatScores}
                  edgeHeatScores={edgeHeatScores}
                  heatGamma={heatGamma}
                  selectedId={selectedId}
                  onSelect={(id) => {
                    setSelectedId(id);
                    if (!id) setQueryPanelOpen(false);
                  }}
                  onHover={setHoveredId}
                />
              ) : null}

              {!graph.isLoading && !graph.error ? (
                <div className="kg-stats">
                  <span>{nodes.length} nodes</span>
                  <span className="kg-stats-sep">·</span>
                  <span>{edges.length} edges</span>
                  {participatingIds.size > 0 ? (
                    <>
                      <span className="kg-stats-sep">·</span>
                      <span className="kg-stats-hit">{participatingIds.size} retrieved</span>
                      <button className="kg-clear-btn" onClick={clearHighlight}>
                        ✕
                      </button>
                    </>
                  ) : null}
                  {isIndexing ? (
                    <>
                      <span className="kg-stats-sep">·</span>
                      <span
                        className="kg-spinner-sm"
                        style={{
                          display: 'inline-block',
                          verticalAlign: 'middle',
                          borderTopColor: '#acc3b3',
                        }}
                      />
                      <span className="kg-stats-indexing">
                        {indexingCount > 0
                          ? `Indexing ${indexingCount} doc${indexingCount === 1 ? '' : 's'}…`
                          : 'Sync running…'}
                      </span>
                    </>
                  ) : null}
                </div>
              ) : null}

              {hoveredNode && hoveredNode.id !== selectedNode?.id ? (
                <HoverTooltip
                  node={hoveredNode}
                  heatEnabled={heatEnabled}
                  heatScores={heatScores}
                  heatHits={heatHits}
                  heatQueries={heatQueries}
                  centralityRank={centralityRank}
                  heatGamma={heatGamma}
                />
              ) : null}
            </div>

            {manifestPanelOpen && (activeManifest || docs.length > 0) ? (
              <ManifestPanel
                manifest={activeManifest}
                docs={docs}
                workspaceProgress={workspaceProgress}
                indexingCount={indexingCount}
                failedCount={failedCount}
                onClose={() => setManifestPanelOpen(false)}
                pipelineId={pipelineId}
                versions={versions.data ?? []}
                kgVersion={kgVersion}
                evalRuns={evalRuns.data ?? []}
                evalRunsLoading={evalRuns.isLoading}
                summary={kgSummary}
                summaryLoading={graph.isLoading}
                selectedType={selectedType}
                onSelectType={onSelectType}
                owner={owner}
                lightragVersion={lightragVersion.data ?? undefined}
                lightragRoleDefaults={lightragRoleDefaults.data?.defaults}
                lightragRoleOverride={activeRoleOverride}
                onLlmRoleConfigSaved={() => {
                  // Owner list carries the override field; refetch so the
                  // sidebar redraws with the new persisted value next render.
                  if (ownerKind === 'pipeline') {
                    pipelines.refetch().catch(() => undefined);
                  } else {
                    documentSets.refetch().catch(() => undefined);
                  }
                }}
                onTriggerEval={async (input) => {
                  if (!pipelineId) return;
                  await kg.triggerEval({ ...input, pipelineId });
                  await evalRuns.refetch();
                }}
              />
            ) : null}

            {/* Right sidebar — query result OR selected node */}
            <div
              className={`kg-sidebar${selectedNode || queryPanelOpen ? ' kg-sidebar--open' : ''}`}
            >
              {queryPanelOpen && queryResult ? (
                <QueryResultPanel
                  result={queryResult}
                  nodes={nodes}
                  onClose={() => {
                    setQueryPanelOpen(false);
                    clearHighlight();
                  }}
                  onFocus={focusNode}
                />
              ) : selectedNode ? (
                <NodePanel
                  node={selectedNode}
                  connections={connectedNodes}
                  onClose={() => setSelectedId(null)}
                  onFocus={focusNode}
                />
              ) : null}
            </div>

            {/* Search-by-label widget */}
            <div
              className={`kg-search-widget${
                selectedNode || queryPanelOpen ? ' kg-search-widget--sidebar' : ''
              }`}
            >
              <input
                className="kg-search-input"
                placeholder="Find node…"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onFocus={() => setSearchOpen(true)}
                onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
              />
              {searchOpen && searchResults.length > 0 ? (
                <div className="kg-search-dropdown">
                  {searchResults.map((n) => (
                    <div
                      key={n.id}
                      className="kg-search-item"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        focusNode(n);
                      }}
                    >
                      <span className="kg-dot" style={{ background: n.color }} />
                      <span>{n.label}</span>
                      <span className="kg-search-type">{n.entityType}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          {/* Bottom query bar */}
          <QueryBar
            queryText={queryText}
            setQueryText={setQueryText}
            queryMode={queryMode}
            setQueryMode={setQueryMode}
            queryModeOpen={queryModeOpen}
            setQueryModeOpen={setQueryModeOpen}
            recentQueries={recentQueries}
            showRecentMenu={showRecentMenu}
            setShowRecentMenu={setShowRecentMenu}
            // Gate on the active owner, not on `pipelineId`. Gating on the
            // pipeline left every document-set / workspace KG permanently
            // unqueryable ("Select a pipeline first…") even though the query
            // mutation has always accepted a documentSetId.
            disabled={!owner}
            running={runQuery.isPending}
            onSubmit={() => {
              const t = queryText.trim();
              if (!t) return;
              runQuery.mutate(t);
            }}
            onReplay={(q) => {
              applyQueryResult(q);
              setShowRecentMenu(false);
            }}
            ownerName={
              ownerKind === 'pipeline' ? (pipeline?.name ?? null) : (documentSet?.name ?? null)
            }
            ownerKind={ownerKind}
          />
        </div>
      </div>
    </>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────

interface IPipelineBarProps {
  /**
   * Drop the owner dropdown entirely. Set when the view is pinned to one KG
   * (the workspace Query tab) — the version selector stays.
   */
  hideOwnerPicker?: boolean;
  pipelines: IKgPipeline[] | undefined;
  documentSets: IDocumentSet[] | undefined;
  /** Null while the initial auto-select is in flight. */
  selection: IKgSelection | null;
  onSelect: (sel: IKgSelection) => void;
  versions: IKgVersion[];
  kgVersion: number | null;
  latestVersion: number | null;
  onSelectVersion: (v: number | null) => void;
  heatEnabled: boolean;
  heatScoreCount: number;
  heatGamma: number;
  onToggleHeat: () => void;
  onChangeGamma: (v: number) => void;
  onClearHeat: () => void;
  manifestPanelOpen: boolean;
  onToggleManifest: () => void;
  indexingCount: number;
  /**
   * Coarse "a sync job for this KG is running" flag, from the sync-job
   * layer (docset owners). True even when `indexingCount === 0` — e.g.
   * during Convert / Push, before any docs have been pushed to LightRAG.
   * The Info button's pulse dot fires on either signal.
   */
  syncInFlight: boolean;
  docCount: number;
  onReload: () => void;
  loading: boolean;
}

function PipelineBar(props: IPipelineBarProps) {
  const [pipeOpen, setPipeOpen] = useState(false);
  const [verOpen, setVerOpen] = useState(false);
  const versionLabel =
    props.kgVersion === null
      ? props.latestVersion !== null
        ? `Latest · v${props.latestVersion}`
        : 'Latest'
      : `v${props.kgVersion}`;

  // Resolve the label for the current selection across both lists — falls
  // back to a placeholder while the picker is empty.
  const selection = props.selection;
  const selectedLabel = (() => {
    if (!selection) return 'Select KG';
    if (selection.kind === 'pipeline') {
      const p = props.pipelines?.find((x) => x.id === selection.id);
      return p ? p.name : `Pipeline · ${selection.id.slice(0, 8)}…`;
    }
    const d = props.documentSets?.find((x) => x.id === selection.id);
    return d ? d.name : `Document set · ${selection.id.slice(0, 8)}…`;
  })();

  const hasPipelines = (props.pipelines?.length ?? 0) > 0;
  const hasDocumentSets = (props.documentSets?.length ?? 0) > 0;

  return (
    <div className="kg-pipeline-bar">
      {props.hideOwnerPicker ? null : (
        <>
          <span className="kg-qbar-label">KG</span>
          <Dropdown open={pipeOpen} setOpen={setPipeOpen} label={selectedLabel}>
            {!hasPipelines && !hasDocumentSets ? (
              <div className="kg-dropdown-item" style={{ opacity: 0.5 }}>
                No pipelines or document sets
              </div>
            ) : (
              <>
                {hasPipelines ? (
                  <div className="kg-dropdown-group-label kg-dropdown-group-label--first">
                    Pipelines
                  </div>
                ) : null}
                {props.pipelines?.map((p) => {
                  const active = selection?.kind === 'pipeline' && selection.id === p.id;
                  return (
                    <div
                      key={`pipeline:${p.id}`}
                      className={`kg-dropdown-item${active ? ' kg-dropdown-item--active' : ''}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        props.onSelect({ kind: 'pipeline', id: p.id });
                        setPipeOpen(false);
                      }}
                    >
                      {p.name}
                    </div>
                  );
                })}
                {hasDocumentSets ? (
                  <div
                    className={`kg-dropdown-group-label${
                      hasPipelines ? '' : ' kg-dropdown-group-label--first'
                    }`}
                  >
                    Document sets
                  </div>
                ) : null}
                {props.documentSets?.map((d) => {
                  const active = selection?.kind === 'documentSet' && selection.id === d.id;
                  return (
                    <div
                      key={`documentSet:${d.id}`}
                      className={`kg-dropdown-item${active ? ' kg-dropdown-item--active' : ''}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        props.onSelect({ kind: 'documentSet', id: d.id });
                        setPipeOpen(false);
                      }}
                    >
                      {d.name}
                    </div>
                  );
                })}
              </>
            )}
          </Dropdown>
        </>
      )}

      {props.versions.length > 0 ? (
        <>
          <span className="kg-qbar-label" style={{ marginLeft: 8 }}>
            Version
          </span>
          <Dropdown open={verOpen} setOpen={setVerOpen} label={versionLabel}>
            <div
              className={`kg-dropdown-item${
                props.kgVersion === null ? ' kg-dropdown-item--active' : ''
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                props.onSelectVersion(null);
                setVerOpen(false);
              }}
            >
              Latest
              {props.latestVersion !== null ? ` · v${props.latestVersion}` : ''}
            </div>
            {props.versions.map((v) => (
              <div
                key={v.id}
                className={`kg-dropdown-item${
                  props.kgVersion === v.version ? ' kg-dropdown-item--active' : ''
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  props.onSelectVersion(v.version);
                  setVerOpen(false);
                }}
              >
                v{v.version}
                <span className="kg-version-date">
                  {new Date(v.createdAt).toLocaleDateString()}
                </span>
              </div>
            ))}
          </Dropdown>
        </>
      ) : null}

      <button
        type="button"
        className={`kg-docs-toggle${props.heatEnabled ? ' kg-docs-toggle--heat' : ''}`}
        style={{ marginLeft: 'auto' }}
        title={
          props.heatEnabled
            ? 'Heat overlay on — nodes + connections shaded by query participation'
            : 'Toggle heat overlay — visualize which graph regions answer your queries'
        }
        onClick={props.onToggleHeat}
      >
        {props.heatEnabled ? '◉' : '○'} Heat
        {props.heatScoreCount ? ` · ${props.heatScoreCount}` : ''}
      </button>

      {props.heatEnabled ? (
        <div className="kg-heat-controls" title="Sensitivity (γ): lower = mid-heat is emphasized">
          <span className="kg-heat-controls-label">γ</span>
          <input
            type="range"
            min={0.2}
            max={1}
            step={0.05}
            value={props.heatGamma}
            onChange={(e) => props.onChangeGamma(parseFloat(e.target.value))}
          />
          <span className="kg-heat-controls-val">{props.heatGamma.toFixed(2)}</span>
          {props.heatScoreCount > 0 ? (
            <button
              type="button"
              className="kg-heat-clear"
              title="Clear heat history"
              onClick={props.onClearHeat}
            >
              ✕
            </button>
          ) : null}
        </div>
      ) : null}

      <button
        type="button"
        className={`kg-docs-toggle${props.manifestPanelOpen ? ' kg-docs-toggle--active' : ''}${
          props.indexingCount > 0 || props.syncInFlight ? ' kg-docs-toggle--indexing' : ''
        }`}
        style={{ marginLeft: 8 }}
        onClick={props.onToggleManifest}
      >
        {props.indexingCount > 0 || props.syncInFlight ? <span className="kg-pulse-dot" /> : null}
        Info{props.docCount ? ` · ${props.docCount} docs` : ''}
      </button>

      <button
        type="button"
        className="kg-icon-btn"
        title="Reload graph and versions"
        style={{ marginLeft: 8, opacity: 0.6 }}
        disabled={props.loading}
        onClick={props.onReload}
      >
        ↺
      </button>
    </div>
  );
}

function Dropdown({
  open,
  setOpen,
  label,
  children,
}: {
  open: boolean;
  setOpen: (v: boolean) => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`kg-dropdown${open ? ' kg-dropdown--open' : ''}`}>
      <button
        type="button"
        className="kg-dropdown-btn"
        onClick={() => setOpen(!open)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
      >
        {label}
        <span className="kg-dropdown-caret">{open ? '▲' : '▼'}</span>
      </button>
      {open ? <div className="kg-dropdown-menu">{children}</div> : null}
    </div>
  );
}

interface IHoverTooltipProps {
  node: IGraphNode;
  heatEnabled: boolean;
  heatScores: Map<string, number>;
  heatHits: Map<string, number>;
  heatQueries: Map<string, IQueryResult[]>;
  centralityRank: Map<string, number>;
  heatGamma: number;
}

function HoverTooltip(props: IHoverTooltipProps) {
  const { node, heatEnabled, heatScores, heatHits, heatQueries, centralityRank, heatGamma } = props;
  if (!heatEnabled) {
    return (
      <div className="kg-tooltip">
        <span className="kg-tooltip-type" style={{ color: node.color }}>
          {node.entityType || 'entity'}
        </span>
        {node.label}
      </div>
    );
  }
  const hits = heatHits.get(node.id) ?? 0;
  const decayed = heatScores.get(node.id) ?? 0;
  let maxScore = 0;
  for (const v of heatScores.values()) if (v > maxScore) maxScore = v;
  const normalized = maxScore > 0 ? decayed / maxScore : 0;
  const damped = maxScore > 0 ? Math.pow(normalized, heatGamma) : 0;
  const rank = centralityRank.get(node.id);
  const recent = heatQueries.get(node.id) ?? [];

  return (
    <div className="kg-tooltip kg-tooltip--heat">
      <div className="kg-tooltip-row">
        <span className="kg-tooltip-type" style={{ color: node.color }}>
          {node.entityType || 'entity'}
        </span>
        <span className="kg-tooltip-label">{node.label}</span>
      </div>
      <div className="kg-tooltip-stats">
        <span className="kg-tooltip-stat">
          hits <b>{hits}</b>
        </span>
        <span className="kg-tooltip-stat">
          heat <b>{damped.toFixed(2)}</b>
        </span>
        <span className="kg-tooltip-stat" title="Decayed score (raw)">
          score <b>{decayed.toFixed(2)}</b>
        </span>
        {rank ? (
          <span className="kg-tooltip-stat" title="Degree-centrality rank (1 = most connected)">
            rank <b>#{rank}</b>
            <span className="kg-tooltip-stat-sub">/{centralityRank.size}</span>
          </span>
        ) : null}
      </div>
      {recent.length ? (
        <div className="kg-tooltip-recent">
          <span className="kg-tooltip-recent-label">recent queries</span>
          <ul>
            {recent.slice(0, 3).map((q, i) => (
              <li key={i}>
                <span className="kg-tooltip-recent-ts">{q.ts}</span>
                <span className="kg-tooltip-recent-text">{q.text}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="kg-tooltip-recent kg-tooltip-recent--empty">
          No queries yet — run a search to see this node light up.
        </div>
      )}
    </div>
  );
}

interface IQueryResultPanelProps {
  result: IQueryResult;
  nodes: IGraphNode[];
  onClose: () => void;
  onFocus: (n: IGraphNode) => void;
}

function QueryResultPanel({ result, nodes, onClose, onFocus }: IQueryResultPanelProps) {
  return (
    <div className="kg-panel">
      <div className="kg-panel-header">
        <div className="kg-eyebrow">Semantic Query</div>
        <button type="button" className="kg-icon-btn" onClick={onClose}>
          ✕
        </button>
      </div>
      <div className="kg-panel-body">
        <div className="kg-query-text">&ldquo;{result.text}&rdquo;</div>
        <div className="kg-meta">
          <span>{result.mode}</span>
          <span className="kg-stats-sep">·</span>
          <span>{result.ts}</span>
        </div>
        <div className="kg-answer">{result.answer}</div>
        {result.entities.length ? (
          <>
            <div className="kg-eyebrow" style={{ marginTop: 16 }}>
              Retrieved entities · {result.entities.length}
            </div>
            <div className="kg-entity-list">
              {result.entities.slice(0, 30).map((label) => {
                const node = nodes.find((n) => n.label === label);
                return (
                  <div
                    key={label}
                    className={`kg-entity-item${node ? ' kg-entity-item--link' : ''}`}
                    onClick={() => node && onFocus(node)}
                  >
                    {/*
                      Fallback when the referenced entity isn't in the
                      currently-loaded graph window (semantic search can
                      surface entities outside the visible subset). Derive
                      a deterministic color from the label so the same
                      entity always renders the same color, instead of an
                      uninformative grey dot.
                    */}
                    <span
                      className="kg-dot"
                      style={{ background: node?.color ?? colorForEntityType(label) }}
                    />
                    {label}
                  </div>
                );
              })}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

interface INodePanelProps {
  node: IGraphNode;
  connections: { peer: IGraphNode; dir: '→' | '←'; desc: string }[];
  onClose: () => void;
  onFocus: (n: IGraphNode) => void;
}

function NodePanel({ node, connections, onClose, onFocus }: INodePanelProps) {
  return (
    <div className="kg-panel">
      <div className="kg-panel-header">
        <div className="kg-eyebrow">Node · {node.entityType || 'entity'}</div>
        <button type="button" className="kg-icon-btn" onClick={onClose}>
          ✕
        </button>
      </div>
      <div className="kg-panel-body">
        <h4 className="kg-node-title" style={{ color: node.color }}>
          {node.label}
        </h4>
        {node.description ? (
          <p className="kg-node-desc">{node.description.split('\n')[0]}</p>
        ) : null}
        <div className="kg-eyebrow" style={{ marginTop: 16 }}>
          Connections · {connections.length}
        </div>
        <div className="kg-entity-list">
          {connections.length ? (
            connections.slice(0, 40).map((r, i) => (
              <div
                key={i}
                className="kg-entity-item kg-entity-item--link"
                onClick={() => onFocus(r.peer)}
              >
                <span className="kg-dot" style={{ background: r.peer.color }} />
                <span
                  style={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {r.peer.label}
                </span>
                <span className="kg-dir">{r.dir}</span>
              </div>
            ))
          ) : (
            <div className="kg-empty-msg">Isolated node.</div>
          )}
        </div>
      </div>
    </div>
  );
}

interface IManifestPanelProps {
  manifest: IKgVersion | null;
  docs: IDocInfo[];
  workspaceProgress: IKgWorkspaceProgress | null;
  indexingCount: number;
  failedCount: number;
  onClose: () => void;
  pipelineId: string | null;
  versions: IKgVersion[];
  kgVersion: number | null;
  evalRuns: IEvalRunSummary[];
  evalRunsLoading: boolean;
  onTriggerEval: (input: { kgVersion?: number; modes: string[] }) => Promise<void>;
  summary: IKgSummary | null;
  summaryLoading: boolean;
  /** Currently-highlighted entity type (lower-case match against
   *  node.entityType) or null. Click a type badge in the Entity
   *  types section to toggle. */
  selectedType: string | null;
  onSelectType: (type: string | null) => void;
  /** Owner + kgVersion used to fetch per-doc chunks when a doc row is clicked. */
  owner: IKgOwner | null;
  /** LightRAG version string (e.g. "1.5.3"). */
  lightragVersion?: string;
  /** LightRAG env-level default model per role (`extract|keyword|query|vlm`). */
  lightragRoleDefaults?: ILightragLlmRoleConfig;
  /** Per-pipeline override map — wins over `lightragRoleDefaults` when present. */
  lightragRoleOverride?: ILightragLlmRoleConfig;
  /** Fired after a successful PATCH so the parent can refetch the pipeline list. */
  onLlmRoleConfigSaved?: () => void;
}

/** Roles displayed in the "LLM Roles" section. Order matches the user-facing
 *  story for eval comparisons: extract drives KG quality, query drives answer
 *  quality, keyword + vlm are secondary. */
const lightragLlmRoles = ['extract', 'keyword', 'query', 'vlm'] as const;

/**
 * Roles that affect the KG **build** (entity extraction / vision parsing) —
 * changing them requires a full re-sync because the persisted graph was
 * extracted under the prior model. The KG-page sidebar surfaces these as
 * read-only and points the operator at the pipeline settings page; the
 * settings page lets them edit + trigger the resync atomically.
 */
const lightragBuildTimeRoles = new Set<string>(['extract', 'vlm']);

/**
 * Native-<details> disclosure wrapper for Info-sidebar sections.
 * Collapsed by default to keep the panel scannable on first open;
 * the user expands the section they want to look at. Using
 * <details>/<summary> instead of a useState toggle gives us correct
 * keyboard accessibility (Enter/Space toggle) and free per-section
 * state without managing an open-set in React.
 */
function CollapsibleSection({
  title,
  summaryValue,
  defaultOpen = false,
  children,
}: {
  title: React.ReactNode;
  /** Inline value displayed to the right of the eyebrow heading
   *  (e.g. " · 16" for entity types, " · 8,434 chars" for extraction
   *  context). Renders as a low-emphasis suffix so the heading stays
   *  the visual anchor. */
  summaryValue?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  return (
    <details className="kg-manifest-section kg-collapse" open={defaultOpen || undefined}>
      <summary className="kg-collapse-summary">
        <span className="kg-collapse-arrow" aria-hidden="true">
          ▸
        </span>
        <span className="kg-eyebrow">{title}</span>
        {summaryValue !== undefined ? (
          <span className="kg-collapse-summary-value">{summaryValue}</span>
        ) : null}
      </summary>
      <div className="kg-collapse-body">{children}</div>
    </details>
  );
}

/** Non-collapsible single-row section: heading on the left, value on
 *  the right. Used for fields where there's no extra content worth
 *  hiding (Domain, Synced). */
function InlineSection({ title, value }: { title: React.ReactNode; value: React.ReactNode }) {
  return (
    <div className="kg-manifest-section kg-inline-section">
      {/* Invisible chevron-width gutter so the heading lines up with
          the collapsible sections below. Preserves left-edge
          consistency across both row variants. */}
      <span className="kg-collapse-arrow" aria-hidden="true" style={{ visibility: 'hidden' }}>
        ▸
      </span>
      <span className="kg-eyebrow">{title}</span>
      <span className="kg-inline-section-value">{value}</span>
    </div>
  );
}

/**
 * Per-role LLM model display + editor. "Effective" = env defaults overlaid
 * with the active pipeline/docset's lightragLlmRoleConfig override. Roles that
 * fall back to the default render in muted color so eval operators can
 * immediately see which roles are diverging.
 *
 * In edit mode, each row exposes a free-text input + a "use default" button.
 * Saving PATCHes the owner's `llm-role-config` endpoint with
 * `applyToCurrentWorkspace: true`, so query-time roles take effect on the
 * next query without a re-sync. Extract changes require a full re-sync —
 * surfaced inline so operators don't get bitten by stale extraction.
 */
function LlmRoleSection({
  defaults,
  override,
  owner,
  onSaved,
}: {
  defaults: ILightragLlmRoleConfig;
  override: ILightragLlmRoleConfig;
  owner: IKgOwner | null;
  onSaved?: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<ILightragLlmRoleConfig>(override);
  const [error, setError] = useState<string | null>(null);
  const { token } = useAuth();

  // Re-sync the draft buffer to the latest persisted override every time the
  // editor opens; otherwise toggling edit→cancel→edit would resurrect a stale
  // draft from a previous session.
  useEffect(() => {
    if (isEditing) setDraft(override);
  }, [isEditing, override]);

  const save = useMutation({
    mutationFn: async (config: ILightragLlmRoleConfig) => {
      if (!owner) throw new Error('No active pipeline / document set');
      const path =
        owner.kind === 'pipeline'
          ? `pipeline/${owner.pipelineId}/llm-role-config`
          : `document-sets/${owner.documentSetId}/llm-role-config`;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(`${metaphysicsBaseUrl}/${path}`, {
        method: 'PATCH',
        headers,
        credentials: 'omit',
        body: JSON.stringify({ config, applyToCurrentWorkspace: true }),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}${txt ? `: ${txt}` : ''}`);
      }
      return (await res.json()) as { appliedToWorkspace: string | null };
    },
    onSuccess: () => {
      setIsEditing(false);
      setError(null);
      onSaved?.();
    },
    onError: (err) => setError(err instanceof Error ? err.message : String(err)),
  });

  const hasAnyOverride = lightragLlmRoles.some((role) => !!override[role]);

  return (
    <CollapsibleSection
      title="LLM Roles"
      summaryValue={hasAnyOverride ? 'overridden' : 'env defaults'}
      defaultOpen={hasAnyOverride || isEditing}
    >
      <div
        className="kg-cost-subhead"
        style={{ marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}
      >
        <span>Per-role models</span>
        {!isEditing && owner ? (
          <button
            type="button"
            className="kg-btn kg-btn--ghost"
            style={{ padding: '0 6px', fontSize: 11 }}
            onClick={() => setIsEditing(true)}
          >
            Edit
          </button>
        ) : null}
      </div>

      {lightragLlmRoles.map((role) => {
        const overridden = !!override[role];
        const persistedModel = overridden ? override[role] : (defaults[role] ?? '—');
        const isBuildTime = lightragBuildTimeRoles.has(role);

        // Read-only render path (also used for build-time roles even while the
        // surrounding section is in edit mode — those live on the pipeline
        // settings page because changing them requires a full re-sync).
        if (!isEditing || isBuildTime) {
          return (
            <div key={role} className="kg-cost-row">
              <span className="kg-cost-label">
                {role}
                {isBuildTime ? (
                  <span
                    className="kg-cost-model"
                    title="Build-time role · edit on the pipeline KG-settings page"
                    style={{ marginLeft: 4, opacity: 0.6 }}
                  >
                    🔒
                  </span>
                ) : null}
              </span>
              <span
                className="kg-cost-value"
                style={overridden ? undefined : { opacity: 0.7 }}
                title={overridden ? 'Override' : 'Env default'}
              >
                {persistedModel}
                {overridden ? null : <span className="kg-cost-model"> (default)</span>}
              </span>
            </div>
          );
        }
        // Edit mode — only reached for query-time roles (keyword, query).
        const draftValue = draft[role] ?? '';
        const placeholder = defaults[role] ?? '';
        return (
          <div key={role} className="kg-cost-row" style={{ alignItems: 'center', gap: 6 }}>
            <span className="kg-cost-label" style={{ flexShrink: 0 }}>
              {role}
            </span>
            <input
              type="text"
              className="kg-qbar-input"
              style={{ flex: 1, fontSize: 12, padding: '2px 6px' }}
              value={draftValue}
              placeholder={`default: ${placeholder}`}
              onChange={(e) => setDraft({ ...draft, [role]: e.target.value || undefined })}
              disabled={save.isPending}
            />
            {draftValue ? (
              <button
                type="button"
                className="kg-btn kg-btn--ghost"
                style={{ padding: '0 4px', fontSize: 10 }}
                title="Clear this override (use env default)"
                onClick={() => setDraft({ ...draft, [role]: undefined })}
                disabled={save.isPending}
              >
                ×
              </button>
            ) : null}
          </div>
        );
      })}

      {isEditing ? (
        <>
          {error ? (
            <div className="kg-quality-error" style={{ marginTop: 6 }}>
              {error}
            </div>
          ) : null}
          <div className="kg-quality-actions" style={{ marginTop: 8 }}>
            <button
              type="button"
              className="kg-quality-btn kg-quality-btn--ghost"
              onClick={() => {
                setIsEditing(false);
                setError(null);
              }}
              disabled={save.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="kg-quality-btn kg-quality-btn--primary"
              disabled={save.isPending}
              onClick={() => {
                // Preserve build-time overrides verbatim from the persisted
                // value (they're not editable here) and only write query-time
                // changes from the draft. Strip undefined entries so the
                // server stores a clean object.
                const cleaned: ILightragLlmRoleConfig = {};
                for (const role of lightragLlmRoles) {
                  const isBuildTime = lightragBuildTimeRoles.has(role);
                  const v = isBuildTime ? override[role] : draft[role];
                  if (typeof v === 'string' && v.trim()) cleaned[role] = v.trim();
                }
                save.mutate(cleaned);
              }}
            >
              {save.isPending ? 'Saving…' : 'Save & apply'}
            </button>
          </div>
        </>
      ) : null}
    </CollapsibleSection>
  );
}

function ManifestPanel(props: IManifestPanelProps) {
  const [chunksDoc, setChunksDoc] = useState<IDocInfo | null>(null);
  return (
    <div className="kg-docs-panel kg-manifest-panel">
      <div className="kg-docs-header">
        <span className="kg-eyebrow">
          {props.manifest
            ? `Info · v${props.manifest.version}${props.manifest.kgWorkspace ? '' : ' (legacy)'}`
            : 'Info'}
        </span>
        <button type="button" className="kg-icon-btn" onClick={props.onClose}>
          ✕
        </button>
      </div>
      <div className="kg-manifest-body">
        {props.manifest ? (
          <ManifestSections
            manifest={props.manifest}
            isIndexing={props.indexingCount > 0}
            selectedType={props.selectedType}
            onSelectType={props.onSelectType}
          />
        ) : null}

        <CollapsibleSection
          title="Documents"
          summaryValue={
            <>
              {props.docs.length}
              {props.failedCount > 0 ? (
                <span className="kg-failed-badge" style={{ marginLeft: 6 }}>
                  {props.failedCount} failed
                </span>
              ) : null}
              {props.indexingCount > 0 ? (
                <span className="kg-indexing-badge" style={{ marginLeft: 6 }}>
                  {props.indexingCount} indexing
                </span>
              ) : null}
            </>
          }
        >
          <KgIndexingProgress
            docs={props.docs}
            indexingCount={props.indexingCount}
            failedCount={props.failedCount}
            workspaceProgress={props.workspaceProgress}
          />
          <div className="kg-docs-list">
            {props.docs.length ? (
              // Sort failed docs to the top so operators can see and act on
              // them without scrolling. Indexing docs second (in-progress
              // signal — includes `analyzing` which is also in-flight),
              // then everything else (mostly 'processed').
              [...props.docs]
                .sort((a, b) => {
                  const order = (s: string) =>
                    s === 'failed'
                      ? 0
                      : s === 'processing' || s === 'pending' || s === 'analyzing'
                        ? 1
                        : 2;
                  return order(a.status) - order(b.status);
                })
                .map((doc) => {
                  const failed = doc.status === 'failed';
                  return (
                    <button
                      key={doc.id}
                      type="button"
                      className="kg-doc-item"
                      onClick={() => {
                        if (!failed) setChunksDoc(doc);
                      }}
                      disabled={failed}
                      title={
                        failed
                          ? 'Failed docs have no chunks to view.'
                          : 'View chunks for this document'
                      }
                      style={{
                        background: 'transparent',
                        border: 0,
                        padding: 0,
                        textAlign: 'left',
                        cursor: failed ? 'not-allowed' : 'pointer',
                        width: '100%',
                        font: 'inherit',
                        color: 'inherit',
                      }}
                    >
                      <span className={`kg-status-dot kg-status-dot--${doc.status}`} />
                      <span className="kg-doc-name">{doc.name}</span>
                      {doc.chunks !== null ? (
                        <span className="kg-doc-chunks">{doc.chunks}c</span>
                      ) : null}
                    </button>
                  );
                })
            ) : (
              <div className="kg-empty-msg" style={{ padding: '4px 0' }}>
                No documents indexed yet.
              </div>
            )}
          </div>
        </CollapsibleSection>

        <EntityTypeHistogram summary={props.summary} loading={props.summaryLoading} />

        {/* KG QUALITY (eval-driven) and the raw LightRAG version are
            pipeline-operator concerns — hidden in the document-set / workspace
            view, which is about the knowledge, not the engine. */}
        {props.owner?.kind !== 'documentSet' ? (
          <KgQualityPanel
            evalRuns={props.evalRuns}
            loading={props.evalRunsLoading}
            versions={props.versions}
            kgVersion={props.kgVersion}
            onTriggerEval={props.onTriggerEval}
          />
        ) : null}

        {props.lightragRoleDefaults ? (
          <LlmRoleSection
            defaults={props.lightragRoleDefaults}
            override={props.lightragRoleOverride ?? {}}
            owner={props.owner}
            onSaved={props.onLlmRoleConfigSaved}
          />
        ) : null}

        {props.owner?.kind !== 'documentSet' && props.lightragVersion ? (
          <InlineSection title="LightRAG" value={props.lightragVersion} />
        ) : null}
      </div>

      <ChunksModal
        open={!!chunksDoc}
        onClose={() => setChunksDoc(null)}
        owner={props.owner}
        kgVersion={props.kgVersion}
        docId={chunksDoc?.id ?? null}
        docName={chunksDoc?.name ?? null}
      />
    </div>
  );
}

/**
 * Per-type histogram of the live KG (counts + 3 examples per type),
 * sorted descending by count. Distinct from the `Entity types` section
 * in ManifestSections — that one lists the SCHEMA-declared types
 * (what should exist); this one shows actuals (what extraction
 * produced). Side-by-side, they make missing types obvious.
 */
function EntityTypeHistogram({
  summary,
  loading,
}: {
  summary: IKgSummary | null;
  loading: boolean;
}) {
  if (loading && !summary) {
    return (
      <CollapsibleSection title="Type histogram">
        <div className="kg-empty-msg" style={{ padding: '4px 0' }}>
          Loading…
        </div>
      </CollapsibleSection>
    );
  }
  if (!summary || summary.entityTypes.length === 0) return null;
  const max = summary.entityTypes[0]?.count ?? 1;
  return (
    <CollapsibleSection title="Type histogram">
      <div className="kg-type-histogram">
        {summary.entityTypes.map((t) => {
          const pct = max > 0 ? (t.count / max) * 100 : 0;
          const color = colorForEntityType(t.type);
          return (
            <div key={t.type} className="kg-type-row">
              <div className="kg-type-row-header">
                <span className="kg-dot" style={{ background: color }} />
                <span className="kg-type-name">{t.type}</span>
                <span className="kg-type-count">{t.count.toLocaleString()}</span>
              </div>
              <div className="kg-type-bar-track">
                <div className="kg-type-bar-fill" style={{ width: `${pct}%`, background: color }} />
              </div>
              {t.examples.length > 0 ? (
                <div className="kg-type-examples" title={t.examples.join(', ')}>
                  {t.examples.join(' · ')}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </CollapsibleSection>
  );
}

function ManifestSections({
  manifest,
  isIndexing,
  onSelectType,
  selectedType,
}: {
  manifest: IKgVersion;
  isIndexing: boolean;
  /** Invoked when the user clicks an entity-type badge. Highlights
   *  all graph nodes of that type via the participating-ids
   *  mechanism. Passing the same type again clears. */
  onSelectType: (type: string | null) => void;
  selectedType: string | null;
}) {
  return (
    <>
      <InlineSection title="Domain" value={manifest.domain} />
      {manifest.domainContext ? (
        <CollapsibleSection
          title="Extraction context"
          summaryValue={`${manifest.domainContext.length.toLocaleString()} chars`}
        >
          {/* Render as markdown — extraction contexts use headings, lists
              and inline code formatting that read terribly as plain text. */}
          <div className="kg-manifest-context md-prose">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{manifest.domainContext}</ReactMarkdown>
          </div>
        </CollapsibleSection>
      ) : null}
      <CollapsibleSection title="Entity types" summaryValue={manifest.entityTypes.length}>
        <div className="kg-entity-type-list">
          {manifest.entityTypes.map((t) => {
            const active = selectedType === t.toLowerCase();
            return (
              <button
                key={t}
                type="button"
                className={`kg-type-badge kg-type-badge--clickable${
                  active ? ' kg-type-badge--active' : ''
                }`}
                style={{ borderLeft: `3px solid ${colorForEntityType(t)}` }}
                onClick={() => onSelectType(active ? null : t.toLowerCase())}
                title={`Highlight all ${t} nodes in the graph`}
              >
                {t}
              </button>
            );
          })}
        </div>
      </CollapsibleSection>
      {/*
        "Source artifacts" used to live here as a list of (name, version)
        pairs from the manifest. It duplicates the "Documents" section below
        (which shows the same names plus their LightRAG ingestion status), so
        the redundant section was removed. The per-artifact version is still
        available via the manifest record if a consumer needs it.
      */}
      <CostSection manifest={manifest} isIndexing={isIndexing} />
      <InlineSection title="Synced" value={new Date(manifest.createdAt).toLocaleString()} />
      {!manifest.kgWorkspace ? (
        <div className="kg-manifest-section kg-legacy-note">
          This version predates KG versioning. The graph data may no longer be available in
          LightRAG.
        </div>
      ) : null}
    </>
  );
}

function CostSection({ manifest, isIndexing }: { manifest: IKgVersion; isIndexing: boolean }) {
  // Manifest-generation cost is recorded by the pipeline path but not the
  // document-set direct path (schema generation happens separately) — so the
  // manifest block is optional. The LightRAG extraction block always applies.
  const manifestTracked = manifest.manifestInputTokens != null;
  // Numeric columns can arrive as strings over JSON — coerce so arithmetic and
  // `.toFixed` don't blow up (they did on the document-set path, which reaches
  // this code now that the manifest-token early-return is gone).
  const toNum = (v: number | string | null | undefined): number | null =>
    v == null || v === '' || Number.isNaN(Number(v)) ? null : Number(v);
  const manifestCost = toNum(manifest.manifestCostUsd);
  const lightragCost = toNum(manifest.lightragCostUsd);
  const totalCost =
    manifestCost !== null && lightragCost !== null
      ? manifestCost + lightragCost
      : (manifestCost ?? lightragCost);
  // True only when LightRAG actually reported back usage stats for
  // this workspace. The poller fills these columns asynchronously; when
  // missing, render the LightRAG block as "indexing…" / "not tracked".
  const lightragTracked = manifest.lightragLlmInputTokens != null;

  // Nothing to show only when neither part has data and we're not mid-index.
  if (!manifestTracked && !lightragTracked && !isIndexing) {
    return (
      <CollapsibleSection title="Cost">
        <div className="kg-empty-msg" style={{ padding: '4px 0' }}>
          Not tracked for this version.
        </div>
      </CollapsibleSection>
    );
  }

  const summaryValue = totalCost !== null ? `$${totalCost.toFixed(4)}` : null;
  return (
    <CollapsibleSection title="Cost" summaryValue={summaryValue ?? undefined}>
      {manifestTracked ? (
        <>
          <div className="kg-cost-subhead">Manifest generation · Claude haiku</div>
          <div className="kg-cost-row">
            <span className="kg-cost-label">Input tokens</span>
            <span className="kg-cost-value">
              {manifest.manifestInputTokens?.toLocaleString() ?? '—'}
            </span>
          </div>
          <div className="kg-cost-row">
            <span className="kg-cost-label">Output tokens</span>
            <span className="kg-cost-value">
              {manifest.manifestOutputTokens?.toLocaleString() ?? '—'}
            </span>
          </div>
          {manifestCost !== null ? (
            <div className="kg-cost-row">
              <span className="kg-cost-label">Subtotal</span>
              <span className="kg-cost-value">${manifestCost.toFixed(6)}</span>
            </div>
          ) : null}
        </>
      ) : null}

      <div className="kg-cost-subhead" style={{ marginTop: manifestTracked ? 8 : 0 }}>
        LightRAG extraction
        {manifest.lightragLlmModel ? ` · ${manifest.lightragLlmModel}` : ''}
      </div>
      {lightragTracked ? (
        <>
          <div className="kg-cost-row">
            <span className="kg-cost-label">LLM input tokens</span>
            <span className="kg-cost-value">
              {manifest.lightragLlmInputTokens?.toLocaleString() ?? '—'}
            </span>
          </div>
          <div className="kg-cost-row">
            <span className="kg-cost-label">LLM output tokens</span>
            <span className="kg-cost-value">
              {manifest.lightragLlmOutputTokens?.toLocaleString() ?? '—'}
            </span>
          </div>
          <div className="kg-cost-row">
            <span className="kg-cost-label">Embedding tokens</span>
            <span className="kg-cost-value">
              {manifest.lightragEmbeddingTokens?.toLocaleString() ?? '—'}
              {manifest.lightragEmbeddingModel ? (
                <span className="kg-cost-model"> ({manifest.lightragEmbeddingModel})</span>
              ) : null}
            </span>
          </div>
          {lightragCost !== null ? (
            <div className="kg-cost-row">
              <span className="kg-cost-label">Subtotal</span>
              <span className="kg-cost-value">${lightragCost.toFixed(6)}</span>
            </div>
          ) : null}
        </>
      ) : (
        <div className="kg-cost-row">
          <span className="kg-cost-label">Status</span>
          <span className="kg-cost-value kg-cost-untracked">
            {isIndexing ? 'indexing…' : 'not tracked'}
          </span>
        </div>
      )}

      {totalCost !== null ? (
        <>
          <div className="kg-cost-divider" />
          <div className="kg-cost-row kg-cost-row--total">
            <span className="kg-cost-label">Total</span>
            <span className="kg-cost-value">${totalCost.toFixed(6)}</span>
          </div>
        </>
      ) : null}
    </CollapsibleSection>
  );
}

interface IKgQualityPanelProps {
  evalRuns: IEvalRunSummary[];
  loading: boolean;
  versions: IKgVersion[];
  kgVersion: number | null;
  onTriggerEval: (input: { kgVersion?: number; modes: string[] }) => Promise<void>;
}

const metricOrder = [
  'factual_correctness',
  'faithfulness',
  'answer_relevancy',
  'context_recall',
  'context_precision',
];

function metricShort(m: string): string {
  return m
    .replace('factual_correctness', 'FC')
    .replace('faithfulness', 'Faith')
    .replace('answer_relevancy', 'AR')
    .replace('context_recall', 'CR')
    .replace('context_precision', 'CP');
}

function evalScoreColor(mean: number | null): string {
  if (mean === null) return '#6b7280';
  if (mean >= 0.75) return '#22c55e';
  if (mean >= 0.5) return '#f59e0b';
  return '#ef4444';
}

function KgQualityPanel(props: IKgQualityPanelProps) {
  const [triggerOpen, setTriggerOpen] = useState(false);
  const [triggerVersion, setTriggerVersion] = useState('');
  const [triggerModes, setTriggerModes] = useState('mix');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runs = props.evalRuns;
  const metricNames = metricOrder.filter((m) => runs.some((r) => r.scoreAggregates?.[m] != null));
  const otherMetrics = [
    ...new Set(runs.flatMap((r) => Object.keys(r.scoreAggregates ?? {}))),
  ].filter((m) => !metricOrder.includes(m));
  const allMetrics = [...metricNames, ...otherMetrics];

  return (
    <CollapsibleSection
      title="KG Quality"
      summaryValue={runs.length ? `${runs.length} run(s)` : '0'}
    >
      <div className="kg-quality-header">
        <button
          type="button"
          className="kg-quality-run-btn"
          onClick={() => setTriggerOpen((v) => !v)}
        >
          ▶ Run Eval
        </button>
      </div>

      {triggerOpen ? (
        <div className="kg-quality-trigger-form">
          <div className="kg-eyebrow">Configure eval run</div>
          <label className="kg-quality-field">
            <span>KG Version</span>
            <select
              className="kg-quality-select"
              value={triggerVersion}
              onChange={(e) => setTriggerVersion(e.target.value)}
            >
              <option value="">Latest</option>
              {props.versions.map((v) => (
                <option key={v.id} value={v.version}>
                  v{v.version}
                </option>
              ))}
            </select>
          </label>
          <label className="kg-quality-field">
            <span>Mode</span>
            <select
              className="kg-quality-select"
              value={triggerModes}
              onChange={(e) => setTriggerModes(e.target.value)}
            >
              <option value="mix">mix</option>
              <option value="hybrid">hybrid</option>
              <option value="naive">naive</option>
              <option value="local">local</option>
              <option value="global">global</option>
              <option value="all">all modes</option>
            </select>
          </label>
          <div className="kg-quality-actions">
            <button
              type="button"
              className="kg-quality-btn kg-quality-btn--ghost"
              onClick={() => setTriggerOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="kg-quality-btn kg-quality-btn--primary"
              disabled={running}
              onClick={async () => {
                setRunning(true);
                setError(null);
                try {
                  const ver = triggerVersion ? Number(triggerVersion) : undefined;
                  const modes =
                    triggerModes === 'all'
                      ? ['mix', 'hybrid', 'naive', 'local', 'global']
                      : [triggerModes];
                  await props.onTriggerEval({
                    ...(ver !== undefined ? { kgVersion: ver } : {}),
                    modes,
                  });
                  setTriggerOpen(false);
                } catch (err: unknown) {
                  setError(err instanceof Error ? err.message : String(err));
                } finally {
                  setRunning(false);
                }
              }}
            >
              {running ? 'Starting…' : 'Run'}
            </button>
          </div>
          {error ? <div className="kg-quality-error">{error}</div> : null}
        </div>
      ) : null}

      {props.loading ? (
        <div className="kg-empty-msg" style={{ padding: '4px 0' }}>
          Loading…
        </div>
      ) : runs.length === 0 ? (
        <div className="kg-empty-msg" style={{ padding: '4px 0' }}>
          No eval runs found for this pipeline.
        </div>
      ) : (
        <div className="kg-quality-runs">
          {runs.map((run) => {
            const config = run.config ?? {};
            const kgV = config['kgVersion'];
            const mode = config['mode'];
            const agg = run.scoreAggregates ?? {};
            const total = run.resultCount ?? 0;
            const fcAgg = agg['factual_correctness'] ?? null;
            const passRate = fcAgg?.passRate ?? null;
            const passCount =
              passRate !== null && passRate !== undefined && total > 0
                ? Math.round(passRate * total)
                : null;
            return (
              <div key={run.id} className="kg-quality-run">
                <div className="kg-quality-run-header">
                  <span className="kg-quality-run-title">
                    {kgV != null ? `KG v${String(kgV)}` : 'Run'}
                    {mode ? <span className="kg-quality-run-mode"> · {String(mode)}</span> : null}
                  </span>
                  <span className="kg-quality-run-date">
                    {run.startedAt ? run.startedAt.slice(0, 10) : ''}
                  </span>
                </div>
                <div className="kg-quality-metrics">
                  {allMetrics.map((m) => {
                    const mean = agg[m]?.mean ?? null;
                    if (mean === null || mean === undefined) return null;
                    return (
                      <span
                        key={m}
                        className="kg-quality-metric"
                        style={{ color: evalScoreColor(mean) }}
                      >
                        {metricShort(m)} {mean.toFixed(2)}
                      </span>
                    );
                  })}
                  {passCount !== null ? (
                    <span className="kg-quality-metric" style={{ color: evalScoreColor(passRate) }}>
                      {passCount}/{total} ✓
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </CollapsibleSection>
  );
}

interface IQueryBarProps {
  queryText: string;
  setQueryText: (v: string) => void;
  queryMode: IQueryMode;
  setQueryMode: (v: IQueryMode) => void;
  queryModeOpen: boolean;
  setQueryModeOpen: (v: boolean) => void;
  recentQueries: IQueryResult[];
  showRecentMenu: boolean;
  setShowRecentMenu: (v: boolean) => void;
  disabled: boolean;
  running: boolean;
  onSubmit: () => void;
  onReplay: (q: IQueryResult) => void;
  ownerName: string | null;
  ownerKind: IKgOwnerKind;
}

function QueryBar(props: IQueryBarProps) {
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  return (
    <div className="kg-query-bar">
      {props.showRecentMenu && props.recentQueries.length ? (
        <div className="kg-recent-menu">
          <div className="kg-recent-header">Recent queries · {props.recentQueries.length}</div>
          {props.recentQueries.map((q, i) => (
            <div key={i} className="kg-recent-item" onClick={() => props.onReplay(q)}>
              <span className="kg-dot" style={{ background: '#8b8cf4' }} />
              <span className="kg-recent-q">{q.text}</span>
              <span className="kg-recent-meta">{q.mode}</span>
            </div>
          ))}
        </div>
      ) : null}
      <div className="kg-qbar-inner">
        <span className="kg-qbar-label">Query</span>
        <Dropdown
          open={props.queryModeOpen}
          setOpen={props.setQueryModeOpen}
          label={cap(props.queryMode)}
        >
          {queryModes.map((m) => (
            <div
              key={m}
              className={`kg-dropdown-item${
                props.queryMode === m ? ' kg-dropdown-item--active' : ''
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                props.setQueryMode(m);
                props.setQueryModeOpen(false);
              }}
            >
              {cap(m)}
            </div>
          ))}
        </Dropdown>
        <input
          className="kg-qbar-input"
          placeholder={
            props.disabled
              ? `Select a ${props.ownerKind === 'pipeline' ? 'pipeline' : 'workspace'} first…`
              : `Ask about ${props.ownerName ?? 'this knowledge graph'}…`
          }
          disabled={props.disabled}
          value={props.queryText}
          onChange={(e) => props.setQueryText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') props.onSubmit();
          }}
          onFocus={() => {
            if (props.recentQueries.length) props.setShowRecentMenu(true);
          }}
          onBlur={() => setTimeout(() => props.setShowRecentMenu(false), 150)}
        />
        <button
          type="button"
          className="kg-btn kg-btn--ghost"
          disabled={props.disabled}
          onClick={() => props.setShowRecentMenu(!props.showRecentMenu)}
        >
          Recent
        </button>
        <button
          type="button"
          className="kg-btn kg-btn--primary"
          disabled={props.running || props.disabled}
          onClick={props.onSubmit}
        >
          {props.running ? <span className="kg-spinner-sm" /> : null} Search →
        </button>
      </div>
    </div>
  );
}
